import { Link, Navigate, Outlet, useLocation } from 'react-router';
import type { Permission } from '@ecobox/domain';
import { changeOwnPassword } from '@ecobox/firebase';
import {
  ChoosePasswordForm,
  IconBox,
  IconHome,
  IconInbox,
  IconScan,
  IconTruck,
  IconUser,
  StagingBanner,
  SyncStatus,
  type SyncItem,
} from '@ecobox/ui';
import { DeviceHeartbeat } from './components/DeviceHeartbeat';
import { DriverDataProvider } from './hooks/useDriver';
import { ECOBOX_ENV } from './env';
import { OpsHelpMenu, OpsHelpNudge, OpsHelpProvider } from './help/HelpProvider';
import { useBranch, useLedger, useServices, useSession, useSyncState } from './services';
import { usePermissions } from './sessions';

interface Tab {
  to: string;
  label: string;
  Icon: typeof IconHome;
  /** Shown when the person holds any of these. No list means everyone. */
  anyOf?: readonly Permission[];
}

// Warehouse staff get Load and Receive, drivers do not (05 §5.2). The server checks again.
const TABS: readonly Tab[] = [
  { to: '/today', label: 'Today', Icon: IconHome },
  { to: '/load', label: 'Load', Icon: IconBox, anyOf: ['trip.load', 'container.load'] },
  { to: '/receive', label: 'Receive', Icon: IconInbox, anyOf: ['trip.unload'] },
  { to: '/handover', label: 'Hand over', Icon: IconTruck, anyOf: ['partner.handover'] },
  { to: '/scan', label: 'Scan', Icon: IconScan },
  { to: '/me', label: 'Me', Icon: IconUser },
];

export function RequireStaff() {
  const session = useSession();
  const services = useServices();
  const location = useLocation();
  // Cached session: signed_in with claims read from the stored token works with no signal.
  if (session.status === 'loading' || (session.status === 'signed_in' && !session.claims)) {
    return <p style={{ padding: 24 }}>Loading…</p>;
  }
  if (session.status === 'signed_out') {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }
  if (!session.isStaff) {
    return (
      <p role="alert" style={{ padding: 24 }}>
        This account is not active. Ask your manager.
      </p>
    );
  }
  if (session.profile?.mustChangePassword) {
    return (
      <div style={{ padding: 16 }}>
        <ChoosePasswordForm
          size="touch"
          onSubmit={async (password) => {
            await changeOwnPassword(services.client.functions, password);
          }}
        />
      </div>
    );
  }
  return <Outlet />;
}

export function toSyncItems(entries: ReturnType<typeof useLedger>): SyncItem[] {
  return entries.map((e) => ({
    id: e.id,
    type: e.type,
    state: e.state,
    createdAt: e.createdAt,
    error: e.lastError?.message,
  }));
}

function HeaderSync() {
  const sync = useSyncState();
  const entries = useLedger();
  const { writer } = useServices();
  return (
    <SyncStatus
      online={sync.online}
      offlineSince={sync.online ? null : sync.since}
      waiting={sync.pending}
      uploads={sync.uploads}
      rejected={sync.rejected}
      notDelivered={sync.notDelivered}
      lastSyncedAt={sync.lastSyncedAt}
      items={toSyncItems(entries)}
      onResend={(id) => void writer.resend(id)}
      onDismiss={(id) => void writer.dismiss(id)}
    />
  );
}

export function Layout() {
  const { branch: store } = useServices();
  const branch = useBranch();
  const { pathname } = useLocation();
  const perms = usePermissions();
  const tabs = TABS.filter((t) => !t.anyOf || t.anyOf.some((p) => perms.has(p)));
  return (
    <DriverDataProvider>
      <DeviceHeartbeat />
      <OpsHelpProvider>
        <div className="ops">
          <StagingBanner env={ECOBOX_ENV} />
          <header className="ops-header">
            <span className="ops-header__brand">EcoBox Ops</span>
            <span className="ops-header__spacer" />
            {branch.current ? (
              <select
                aria-label="Branch"
                value={branch.current}
                onChange={(e) => store.select(e.target.value)}
              >
                {branch.options.map((b) => (
                  <option key={b} value={b}>
                    {b}
                  </option>
                ))}
              </select>
            ) : null}
            <OpsHelpMenu />
            <HeaderSync />
          </header>
          <main className="ops-main">
            <OpsHelpNudge />
            <Outlet />
          </main>
          <nav
            className="ops-tabs"
            aria-label="Main"
            style={{ gridTemplateColumns: `repeat(${tabs.length}, 1fr)` }}
          >
            {tabs.map(({ to, label, Icon }) => (
              <Link
                key={to}
                to={to}
                className="ops-tab"
                aria-current={pathname === to || pathname.startsWith(`${to}/`) ? 'page' : undefined}
              >
                <Icon size={26} />
                {label}
              </Link>
            ))}
          </nav>
        </div>
      </OpsHelpProvider>
    </DriverDataProvider>
  );
}
